/**
 * Client-side error reporter.
 *
 * Captures errors from four sources and forwards them to the server in
 * batched POSTs:
 *   - `window.onerror`               (uncaught synchronous errors)
 *   - `window.onunhandledrejection`  (uncaught promise rejections)
 *   - `report(...)` (called by Solid ErrorBoundary + tRPC link)
 *
 * The server dedups by `fingerprint = sha256(message + first stack frame)`
 * so we don't need to dedup occurrence-by-occurrence here — but we DO
 * coalesce by fingerprint within a single flush window (and apply a cap)
 * so a runaway loop can't blow up our own batch payload.
 *
 * Reporter never throws and never logs to console (would echo back through
 * window.onerror and re-enter the reporter). All internal errors are
 * swallowed.
 */

import { trpc } from './trpc'

type Source = 'window' | 'unhandled' | 'boundary' | 'trpc'

interface PendingReport {
  fingerprint: string
  source: Source
  message: string
  stack: string | null
  route: string
  user_agent: string
}

const FLUSH_INTERVAL_MS = 5_000
const MAX_BUFFER = 20
const MAX_REPORTS_PER_MINUTE = 60
const MESSAGE_MAX = 1024
const STACK_MAX = 4096

const buffer = new Map<string, PendingReport>()
let timer: ReturnType<typeof setTimeout> | null = null
let installed = false
let droppedSinceFlush = 0
const recentSendTimestamps: number[] = []

function clamp(s: string | null | undefined, max: number): string {
  if (!s) return ''
  return s.length <= max ? s : s.slice(0, max)
}

/** Hex sha-256 over `${message}\n${firstStackFrame}`. Sync-friendly via
 *  subtle.digest; the call is async but we don't await it inline so error
 *  capture stays non-blocking. */
async function fingerprintFor(message: string, stack: string | null): Promise<string> {
  const firstFrame = (stack ?? '').split('\n').slice(0, 1).join('').trim()
  const data = new TextEncoder().encode(`${message}\n${firstFrame}`)
  try {
    const buf = await crypto.subtle.digest('SHA-256', data)
    return [...new Uint8Array(buf)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    // Older browsers / non-secure contexts: fall back to a non-crypto hash.
    let h = 2166136261
    for (let i = 0; i < data.length; i++) {
      h ^= data[i]
      h = Math.imul(h, 16777619)
    }
    return ('00000000' + (h >>> 0).toString(16)).slice(-8).repeat(4)
  }
}

function nowMs(): number {
  return Date.now()
}

function withinRateLimit(): boolean {
  const cutoff = nowMs() - 60_000
  while (recentSendTimestamps.length && recentSendTimestamps[0] < cutoff) {
    recentSendTimestamps.shift()
  }
  return recentSendTimestamps.length < MAX_REPORTS_PER_MINUTE
}

function scheduleFlush(): void {
  if (timer != null) return
  timer = setTimeout(() => {
    timer = null
    void flush()
  }, FLUSH_INTERVAL_MS)
}

async function flush(): Promise<void> {
  if (buffer.size === 0) return
  const reports = Array.from(buffer.values()).slice(0, MAX_BUFFER)
  buffer.clear()
  droppedSinceFlush = 0
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (trpc as any).tracking.reportClientErrors.mutate({
      reports: reports.map((r) => ({
        fingerprint: r.fingerprint,
        source: r.source,
        message: r.message,
        stack: r.stack,
        route: r.route,
        user_agent: r.user_agent,
      })),
    })
    for (const _ of reports) recentSendTimestamps.push(nowMs())
  } catch {
    // Swallow: never echo a reporter failure back to the user. We could
    // re-buffer for retry but that risks unbounded growth on a sustained
    // network outage; better to drop and rely on subsequent occurrences
    // refilling the bucket.
  }
}

export function report(
  source: Source,
  message: string | undefined | null,
  stack?: string | null,
): void {
  try {
    if (!message) return
    if (!withinRateLimit()) {
      droppedSinceFlush++
      return
    }
    const cleanMsg = clamp(String(message), MESSAGE_MAX)
    const cleanStack = clamp(stack ?? null, STACK_MAX)
    void fingerprintFor(cleanMsg, cleanStack).then((fp) => {
      // Coalesce by fingerprint within the flush window — a tight error
      // loop should land as one entry, not 200.
      if (buffer.has(fp)) return
      if (buffer.size >= MAX_BUFFER) {
        droppedSinceFlush++
        return
      }
      buffer.set(fp, {
        fingerprint: fp,
        source,
        message: cleanMsg,
        stack: cleanStack || null,
        route: window.location.pathname,
        user_agent: navigator.userAgent.slice(0, 512),
      })
      scheduleFlush()
    })
  } catch {
    // never throw from inside the reporter
  }
}

export function installErrorReporter(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (e: ErrorEvent) => {
    // Skip vite preload errors — entry-client.tsx already handles them by
    // reloading; reporting them adds noise without signal.
    if (/dynamically imported module/i.test(e.error?.message ?? e.message ?? '')) return
    const stack = e.error?.stack ?? null
    const msg = e.error?.message ?? e.message ?? 'Unknown error'
    report('window', msg, stack)
  })

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    let msg: string
    let stack: string | null = null
    if (reason instanceof Error) {
      msg = reason.message
      stack = reason.stack ?? null
    } else if (typeof reason === 'string') {
      msg = reason
    } else {
      try {
        msg = JSON.stringify(reason)
      } catch {
        msg = String(reason)
      }
    }
    report('unhandled', msg, stack)
  })

  // Best-effort flush on page hide so an error captured 100ms before
  // navigation isn't lost. `pagehide` is more reliable than `unload` on
  // mobile browsers.
  window.addEventListener('pagehide', () => {
    void flush()
  })
}

/** Manual hook for tRPC failures and ErrorBoundary fallbacks. */
export function reportTrpcError(message: string, stack?: string | null): void {
  report('trpc', message, stack ?? null)
}

export function reportBoundaryError(err: unknown): void {
  if (err instanceof Error) report('boundary', err.message, err.stack ?? null)
  else report('boundary', String(err))
}
